import { BaseWebComponentView } from '../../../../framework/BaseWebComponentView';
import { TagDetailsFormViewData } from '../types/TagDetailsFormViewData';
import { GzTitleWebComponent } from '../../../../ui/gzTitle';
import { GzFormFieldWebComponent } from '../../../../ui/gzForm/formField';
import { GzRadioGroupWebComponent, GzRadioWebComponent } from '../../../../ui/gzRadio';
import { GzInputWebComponent } from '../../../../ui/gzInput/component/GzInputWebComponent';
import { GzTextareaWebComponent } from '../../../../ui/gzInput';
import { GzCheckboxWebComponent } from '../../../../ui/gzCheckbox';
import { GzDropdownWebComponent } from '../../../../ui/gzDropdown';
import { TemplateState } from '../../../../types/common/types';
import { TagActionContext } from '../../types/TagActionContext';
import { FormGroup } from '../../../../ui/gzForm/formGroup/form-group';
import { OsTypeData } from '../../../types/os-type-data';
import { EndpointTypeData } from '../../../types/endpoint-type-data';
import { Dependencies } from '../../../types/dependencies';
import { toggleVisibility } from '../../../../utils/htmlElement.utils';
import { TagTypes } from '../../../types/tag-types';
import { Company, TagData } from '../../../types/tag-data';
import { RequiredValidator } from '../../../../ui/gzForm/validators/required.validator';
import { MaxLengthValidator } from '../../../../ui/gzForm/validators/max-length.validator';
import { RemoteValidator } from '../../../../ui/gzForm/validators/remote.validator';
import { CustomInclusiveDisjunctionValidator } from '../../../../ui/gzForm/validators/custom-inclusive-disjunction.validator';
import { IpValidator } from '../../../../ui/gzForm/validators/ip.validator';
import { RegexValidator } from '../../../../ui/gzForm/validators/regex.validator';
import { CustomizableValidator } from '../../../../ui/gzForm/validators/customizable.validator';
import { ValidationResult } from '../../../../ui/gzForm/formField/types/form-validator';
import { GzLabeledContentWebComponent } from '../../../../ui/gzLabeledContent';
import moment from 'moment-timezone';
import { API_TYPE_COMPANIES_FOR_GZ_TAGS } from '../../../../webcomponents.constants';
import { GzSingleSelectComplexWebComponent } from '../../../../ui/gzSingleSelectComplex';
import { GetItemsResponse } from '../../../../ui/gzSingleSelectComplex/types';

export class TagDetailsFormView extends BaseWebComponentView {
  public formGroup!: FormGroup;
  public tagRulesFormGroup!: FormGroup;
  private context: TagActionContext;
  private company: Company | undefined;

  public dom: {
    $wrapper: HTMLElement;

    $generalDetailsSubtitle: GzTitleWebComponent;

    $tagTypeFormField: GzFormFieldWebComponent<GzRadioGroupWebComponent, string | number>;
    $tagTypeGroup: GzRadioGroupWebComponent;
    $tagTypeOptionCustom: GzRadioWebComponent;
    $tagTypeOptionAutomatic: GzRadioWebComponent;
    $tagNameFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;
    $tagDescriptionFormField: GzFormFieldWebComponent<GzTextareaWebComponent, string>;
    $tagCompanyFormField: GzFormFieldWebComponent<GzSingleSelectComplexWebComponent, string | number>;

    $editDetailsContainer: HTMLElement;
    $editDetailsTagTypeLabel: GzLabeledContentWebComponent;
    $editDetailsTagTypeInfo: GzTitleWebComponent;
    $editDetailsTagCreatedByLabel: GzLabeledContentWebComponent;
    $editDetailsTagCreatedByInfo: GzTitleWebComponent;
    $editDetailsTagCreatedOnLabel: GzLabeledContentWebComponent;
    $editDetailsTagCreatedOnInfo: GzTitleWebComponent;
    $editDetailsTagModifiedByLabel: GzLabeledContentWebComponent;
    $editDetailsTagModifiedByInfo: GzTitleWebComponent;
    $editDetailsTagLastModifiedLabel: GzLabeledContentWebComponent;
    $editDetailsTagLastModifiedInfo: GzTitleWebComponent;

    $ruleDetailsContainer: HTMLElement;

    $ruleDetailsSubtitle: GzTitleWebComponent;
    $ruleDetailsSubtitleInfo: HTMLElement;

    $rulesValidatorHelper: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

    $endpointNameEnabledFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
    $endpointNameRuleFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;

    $ipEnabledFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
    $ipRuleFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;

    $osEnabledFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
    $osRuleFormField: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;

    $endpointTypeEnabledFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
    $endpointTypeRuleFormField: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;
  };

  public dependencies: Dependencies;

  private static isTagTypeFormFieldVisible(formContext: TagActionContext) {
    return formContext === TagActionContext.Create || formContext === TagActionContext.Clone;
  }

  private static isReadonlyTagInfoVisible(formContext: TagActionContext) {
    return formContext === TagActionContext.Edit;
  }

  getDomElements() {
    this.dom = {
      $wrapper: this.shadow.querySelector('#tag-details-form-wrapper'),

      $generalDetailsSubtitle: this.shadow.querySelector('#general-details-subtitle'),

      $tagTypeFormField: this.shadow.querySelector('#tag-type-form-field'),
      $tagTypeGroup: this.shadow.querySelector('#tag-type-group'),
      $tagTypeOptionCustom: this.shadow.querySelector('#tag-type-option-custom'),
      $tagTypeOptionAutomatic: this.shadow.querySelector('#tag-type-option-automatic'),
      $tagNameFormField: this.shadow.querySelector('#tag-name-form-field'),
      $tagDescriptionFormField: this.shadow.querySelector('#tag-description-form-field'),
      $tagCompanyFormField: this.shadow.querySelector('#tag-company-form-field'),

      $editDetailsContainer: this.shadow.querySelector('#section-edit-details'),
      $editDetailsTagTypeLabel: this.shadow.querySelector('#tag-type-label'),
      $editDetailsTagTypeInfo: this.shadow.querySelector('#tag-type-info'),
      $editDetailsTagCreatedByLabel: this.shadow.querySelector('#tag-created-by-label'),
      $editDetailsTagCreatedByInfo: this.shadow.querySelector('#tag-created-by-info'),
      $editDetailsTagCreatedOnLabel: this.shadow.querySelector('#tag-created-on-label'),
      $editDetailsTagCreatedOnInfo: this.shadow.querySelector('#tag-created-on-info'),
      $editDetailsTagModifiedByLabel: this.shadow.querySelector('#tag-modified-by-label'),
      $editDetailsTagModifiedByInfo: this.shadow.querySelector('#tag-modified-by-info'),
      $editDetailsTagLastModifiedLabel: this.shadow.querySelector('#tag-last-modified-label'),
      $editDetailsTagLastModifiedInfo: this.shadow.querySelector('#tag-last-modified-info'),

      $ruleDetailsContainer: this.shadow.querySelector('#section-rule-details'),

      $ruleDetailsSubtitle: this.shadow.querySelector('#rule-details-subtitle'),
      $ruleDetailsSubtitleInfo: this.shadow.querySelector('#rule-details-subtitle-info'),

      $rulesValidatorHelper: this.shadow.querySelector('#rules-validation-error-message-form-field'),

      $endpointNameEnabledFormField: this.shadow.querySelector('#endpoint-name-enabled-form-field'),
      $endpointNameRuleFormField: this.shadow.querySelector('#endpoint-name-rule-form-field'),

      $ipEnabledFormField: this.shadow.querySelector('#ip-enabled-form-field'),
      $ipRuleFormField: this.shadow.querySelector('#ip-rule-form-field'),

      $osEnabledFormField: this.shadow.querySelector('#os-enabled-form-field'),
      $osRuleFormField: this.shadow.querySelector('#os-rule-form-field'),

      $endpointTypeEnabledFormField: this.shadow.querySelector('#endpoint-type-enabled-form-field'),
      $endpointTypeRuleFormField: this.shadow.querySelector('#endpoint-type-rule-form-field'),
    };

    this.setLoadingState();
    this.getTranslations();
    this.setTranslatedStrings();
  }

  public setLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.LOADING);
  }

  public removeLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
  }

  public getTranslations() {
    this.commonStrings = this.stringsTranslator.getCommonStrings();
  }

  setTranslatedStrings() {
    this.dom.$generalDetailsSubtitle.innerText = this.dependencies.translateFn('General');

    this.dom.$tagTypeFormField.label = this.dependencies.translateFn('Tag type');
    this.dom.$tagTypeOptionCustom.label = this.dependencies.translateFn('Custom');
    this.dom.$tagTypeOptionAutomatic.label = this.dependencies.translateFn('Automatic');

    this.dom.$tagNameFormField.label = this.dependencies.translateFn('Tag name');
    this.dom.$tagNameFormField.Input.placeholder = this.dependencies.translateFn('Type name');

    this.dom.$tagDescriptionFormField.label = this.dependencies.translateFn('Description');
    this.dom.$tagDescriptionFormField.Input.placeholder = this.dependencies.translateFn('Type your description');

    this.dom.$tagCompanyFormField.label = this.dependencies.translateFn('Company');
    this.dom.$tagCompanyFormField.Input.placeholder = this.dependencies.translateFn('Select company');
    this.dom.$tagCompanyFormField.Input.searchPlaceholderString = this.dependencies.translateFn('Search');
    this.dom.$tagCompanyFormField.Input.noSearchResultsString = this.dependencies.translateFn('No results found.');

    this.dom.$editDetailsTagTypeLabel.label = this.dependencies.translateFn('Tag type');
    this.dom.$editDetailsTagCreatedByLabel.label = this.dependencies.translateFn('Created by');
    this.dom.$editDetailsTagCreatedOnLabel.label = this.dependencies.translateFn('Created on');
    this.dom.$editDetailsTagModifiedByLabel.label = this.dependencies.translateFn('Modified by');
    this.dom.$editDetailsTagLastModifiedLabel.label = this.dependencies.translateFn('Last modified');

    this.dom.$ruleDetailsSubtitle.innerText = this.dependencies.translateFn('Rules');
    this.dom.$ruleDetailsSubtitleInfo.innerText = this.dependencies.translateFn(
      'Select at least one rule to automatically assign the tag. The tag applies to the endpoints that match all the selected rules.'
    );

    this.dom.$endpointNameEnabledFormField.Input.label = this.dependencies.translateFn('Endpoint name pattern');
    this.dom.$endpointNameRuleFormField.Input.placeholder =
      this.dependencies.translateFn('Supported wildcards: * and ?');

    this.dom.$ipEnabledFormField.Input.label = this.dependencies.translateFn('IP/CIDR');
    this.dom.$ipRuleFormField.Input.placeholder = this.dependencies.translateFn('Example: 192.168.1.0/24');

    this.dom.$osEnabledFormField.Input.label = this.dependencies.translateFn('Operating system type');
    this.dom.$osRuleFormField.Input.placeholder = this.dependencies.translateFn('Select OS');

    this.dom.$endpointTypeEnabledFormField.Input.label = this.dependencies.translateFn('Endpoint type');
    this.dom.$endpointTypeRuleFormField.Input.placeholder = this.dependencies.translateFn('Select endpoint type');
  }

  setContextSpecificTranslatedStrings(formContext: TagActionContext) {
    this.dom.$generalDetailsSubtitle.innerText =
      formContext === TagActionContext.Create || formContext === TagActionContext.Clone
        ? this.dependencies.translateFn('General')
        : this.dependencies.translateFn('Details');
  }

  buildSuccessView(viewData: TagDetailsFormViewData) {
    this.cacheViewData(viewData);

    this.setContextSpecificTranslatedStrings(viewData.formContext);

    this.setCompanyDropdown(viewData.formContext);

    this.createFormGroup();

    this.initTagTypeValues(viewData.formContext);
    this.setOsTypeDropdownOptions(viewData.osTypes);
    this.setEndpointTypeDropdownOptions(viewData.endpointTypes);

    if (viewData.formContext === TagActionContext.Create) {
      // in this case I want to trigger change events on checkboxes so that rules fields become disabled
      this.dom.$endpointNameEnabledFormField.Input.setFormValue(false);
      this.dom.$ipEnabledFormField.Input.setFormValue(false);
      this.dom.$osEnabledFormField.Input.setFormValue(false);
      this.dom.$endpointTypeEnabledFormField.Input.setFormValue(false);
    }

    this.dom.$rulesValidatorHelper.Input.setFormValue(false);

    this.removeLoadingState();
  }

  buildErrorView(error: Error) {
    this.resetTemplateState(this.dom.$wrapper);
    this.removeLoadingState();

    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  private cacheViewData(viewData: TagDetailsFormViewData) {
    this.context = viewData.formContext;
    this.company = viewData.company;
  }

  private createFormGroup() {
    // set validators
    this.setTagNameValidators(null);
    this.setTagTypeValidators();
    this.setTagDescriptionValidators();
    this.setTagCompanyValidators();
    this.setRulesRequiredCustomValidator();
    this.setTagRulesValidators();

    this.formGroup = FormGroup.build({
      name: this.dom.$tagNameFormField,
      description: this.dom.$tagDescriptionFormField,
      companyId: this.dom.$tagCompanyFormField,
    });

    this.tagRulesFormGroup = FormGroup.build({
      rulesValidatorHelper: this.dom.$rulesValidatorHelper,
      endpointName: {
        enabled: this.dom.$endpointNameEnabledFormField,
      },
      ip: {
        enabled: this.dom.$ipEnabledFormField,
      },
      os: {
        enabled: this.dom.$osEnabledFormField,
      },
      endpointType: {
        enabled: this.dom.$endpointTypeEnabledFormField,
      },
    });
  }

  private initTagTypeValues(formContext: TagActionContext): void {
    const isTagTypeFormFieldVisible = TagDetailsFormView.isTagTypeFormFieldVisible(formContext);

    toggleVisibility(this.dom.$tagTypeFormField, isTagTypeFormFieldVisible);

    this.dom.$tagTypeOptionCustom.value = TagTypes.TAG_TYPE_CUSTOM;
    this.dom.$tagTypeOptionAutomatic.value = TagTypes.TAG_TYPE_AUTOMATIC;

    if (formContext === TagActionContext.Create) {
      // set default value for create tag case
      this.dom.$tagTypeFormField.Input.setFormValue(TagTypes.TAG_TYPE_CUSTOM);
    }
  }

  private setOsTypeDropdownOptions(osTypes: OsTypeData[]) {
    this.dom.$osRuleFormField.Input.options = osTypes.map(osType => {
      return {
        title: osType.name,
        value: osType.value,
      };
    });
  }

  private setEndpointTypeDropdownOptions(endpointTypes: EndpointTypeData[]) {
    this.dom.$endpointTypeRuleFormField.Input.options = endpointTypes.map(endpointType => {
      return {
        title: endpointType.name,
        value: endpointType.value,
      };
    });
  }

  private setCompanyDropdown(formContext: TagActionContext) {
    if (this.dependencies.permissions.canHaveChildCompanies) {
      toggleVisibility(this.dom.$tagCompanyFormField, true);
      this.dom.$tagCompanyFormField.readonly = false;
      this.dom.$tagCompanyFormField.disabled = false;

      this.dom.$tagCompanyFormField.Input.apiType = API_TYPE_COMPANIES_FOR_GZ_TAGS;

      const companyData: GetItemsResponse = {
        items: [],
        totalItemsCount: 0,
      };
      if (this.company) {
        companyData.items.push({
          label: this.company?.name,
          value: this.company?.id,
        });
        (companyData.totalItemsCount as number) += 1;
      }

      this.dom.$tagCompanyFormField.Input.updateComplete.then(() => {
        // set preventBackendCall flag in context of Edit because there is no need to load companies from db
        this.dom.$tagCompanyFormField.Input.controller
          .setFilterOptions(companyData, formContext === TagActionContext.Edit)
          .then()
          .catch();
      });
    } else {
      toggleVisibility(this.dom.$tagCompanyFormField, false);
      this.dom.$tagCompanyFormField.disabled = true;
    }
  }

  private updateTagTypeFormField(formContext: TagActionContext) {
    toggleVisibility(this.dom.$tagTypeFormField, TagDetailsFormView.isTagTypeFormFieldVisible(formContext));

    if (formContext === TagActionContext.Clone) {
      this.dom.$tagTypeFormField.disabled = true;
    }
  }

  private setReadonlyTagDetails(formContext: TagActionContext, tagData: TagData) {
    toggleVisibility(this.dom.$editDetailsContainer, TagDetailsFormView.isReadonlyTagInfoVisible(formContext));

    this.dom.$editDetailsTagTypeInfo.innerText =
      tagData.type === 0 ? this.dependencies.translateFn('Custom') : this.dependencies.translateFn('Automatic');
    this.dom.$editDetailsTagCreatedByInfo.innerText = tagData.ownerFullName || '';
    this.dom.$editDetailsTagCreatedOnInfo.innerText = moment(tagData.created).format('DD MMMM YYYY, HH:mm');
    this.dom.$editDetailsTagModifiedByInfo.innerText = tagData.lastEditedByFullName || '';
    this.dom.$editDetailsTagLastModifiedInfo.innerText = moment(tagData.lastModified).format('DD MMMM YYYY, HH:mm');
  }

  public setTagData(tag: TagData) {
    if (this.context !== TagActionContext.Create) {
      this.dom.$tagTypeFormField.Input.setFormValue(tag.type);
    }

    if (this.context === TagActionContext.Edit) {
      this.dom.$tagCompanyFormField.readonly = true;
      this.setReadonlyTagDetails(this.context, tag);
    }

    this.updateTagTypeFormField(this.context);

    // remove name validators to prevent server side name uniqueness validation before setting value
    this.dom.$tagNameFormField.Validators = [];

    this.formGroup.value = {
      name: tag.name,
      description: tag.description,
      companyId: tag.company.id,
      rules: {
        ip: {
          enabled: tag.rules?.ip?.enabled ?? false,
          rule: tag.rules?.ip?.rule?.toString() || '',
        },
        os: {
          enabled: tag.rules?.os?.enabled ?? false,
          rule: tag.rules?.os?.rule !== undefined ? tag.rules.os.rule : '',
        },
        endpointType: {
          enabled: tag.rules?.endpointType?.enabled ?? false,
          rule: tag.rules?.endpointType?.rule !== undefined ? tag.rules.endpointType.rule : '',
        },
        endpointName: {
          enabled: tag.rules?.endpointName?.enabled ?? false,
          rule: tag.rules?.endpointName?.rule?.toString() || '',
        },
      },
    };

    // replace tag name validators to exclude the current tag's name during the uniqueness check
    this.setTagNameValidators(tag);

    // visual tweak for showing old rules value in disabled fields
    if (this.context === TagActionContext.Edit) {
      if (tag.rules?.ip?.rule !== undefined) {
        this.dom.$ipRuleFormField.Input.setFormValue(tag.rules.ip.rule);
      }
      if (tag.rules?.os?.rule !== undefined) {
        this.dom.$osRuleFormField.Input.setFormValue(tag.rules.os.rule);
      }
      if (tag.rules?.endpointType?.rule !== undefined) {
        this.dom.$endpointTypeRuleFormField.Input.setFormValue(tag.rules.endpointType.rule);
      }
      if (tag.rules?.endpointName?.rule !== undefined) {
        this.dom.$endpointNameRuleFormField.Input.setFormValue(tag.rules.endpointName.rule);
      }
    }
  }

  private setTagNameValidators(tag: TagData | null) {
    this.dom.$tagNameFormField.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      MaxLengthValidator(
        40,
        this.dependencies.translateFn('The name can have a maximum of {tagNameMaxLength} characters.', {
          tagNameMaxLength: 40,
        })
      ),
      RemoteValidator(
        (value: string) =>
          this.dependencies.api.clients.GzTags.CheckTagNameIsUnique(
            value,
            this.dom.$tagCompanyFormField?.value || this.company?.id || this.dependencies.getCurrentUserCompany().id,
            tag?._id || null
          ),
        (response: { success: boolean }) => response.success,
        this.dependencies.translateFn('Tag name already exists in the company.'),
        this.dependencies.translateFn('A communication error has occurred. Please enter the tag name again.')
      ),
    ];
  }

  private setTagTypeValidators() {
    this.dom.$tagTypeFormField.Validators = [RequiredValidator(this.commonStrings.requiredField)];
  }

  private setTagDescriptionValidators() {
    this.dom.$tagDescriptionFormField.Validators = [
      MaxLengthValidator(
        100,
        this.dependencies.translateFn('The tag description must have a maximum of {descriptionMaxLength} characters.', {
          descriptionMaxLength: 100,
        })
      ),
    ];
  }

  private setTagCompanyValidators() {
    this.dom.$tagCompanyFormField.Validators = [RequiredValidator(this.commonStrings.requiredField)];
  }

  private isAnyRuleSelected(): boolean {
    return (
      this.dom.$endpointNameEnabledFormField.Input.getFormValue() ||
      this.dom.$ipEnabledFormField.Input.getFormValue() ||
      this.dom.$osEnabledFormField.Input.getFormValue() ||
      this.dom.$endpointTypeEnabledFormField.Input.getFormValue()
    );
  }

  private setRulesRequiredCustomValidator() {
    this.dom.$rulesValidatorHelper.Validators = [
      CustomInclusiveDisjunctionValidator(
        this.isAnyRuleSelected.bind(this),
        this.dependencies.translateFn('No rule selected.')
      ),
    ];
  }

  private setTagRulesValidators() {
    this.dom.$endpointNameRuleFormField.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      MaxLengthValidator(
        128,
        this.dependencies.translateFn('The name pattern can have a maximum of {maxLength} characters.', {
          maxLength: 128,
        })
      ),
      CustomizableValidator(this.validateEndpointNamePattern.bind(this)),
      RegexValidator(
        /^([a-zA-Z0-9_-]+[?*]?)+$/u,
        this.dependencies.translateFn(
          'Use only Latin letters (a-z and A-Z), digits (0-9), wildcards (* and ?), underscore ( _ ), and hyphen ( - ). Names with more characters must start with a letter or digit. Wildcards must not be consecutive.'
        )
      ),
    ];

    this.dom.$ipRuleFormField.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      IpValidator(this.dependencies.translateFn('Invalid IP/mask format'), false),
    ];

    this.dom.$osRuleFormField.Validators = [RequiredValidator(this.commonStrings.requiredField)];

    this.dom.$endpointTypeRuleFormField.Validators = [RequiredValidator(this.commonStrings.requiredField)];
  }

  private validateEndpointNamePattern(value: string): ValidationResult {
    if (value === '*' || value === '?') {
      return {
        valid: false,
        error: this.dependencies.translateFn('The name pattern cannot be only * or ?.'),
      };
    }

    return {
      valid: true,
    };
  }
}
